import axios from 'axios';
import { useRef, useState } from 'react';
import { useConfig } from '../../../config';
import { useTranslation } from '../../../i18n/i18n';
import { Res, useApi } from '../../../state/api';
import { useSession } from '../../../state/session';
import { ApiDeliveryAddress, ApiList, ApiSchedule, DeliveryTypeCompany } from '../../../types';
import { ContentCard } from '../../ContentCard/ContentCard';
import { AddressSummary } from '../../Form/AddressSummary';
import { Button } from '../../Form/Button';
import { CheckoutSelectDeliveryAddress } from '../../Form/CheckoutSelectDeliveryAddress';
import { DeliveryAddressInput } from '../../Form/DeliveryAddressInput';
import { useFormValidationContainer } from '../../Form/FormValidatorContainer';
import { Icon, close } from '../../Icon/Icon';
import { Modal } from '../../Modal/Modal';
import { WITH_CREDENTIALS } from '../../utils';
import './EditScheduleAddress.css';

type DeliveryAddressPayload = Omit<ApiDeliveryAddress, 'id' | 'primary' | 'lastUsed'>;
type PatchOp = { op: 'replace'; path: string; value: string | number };

export const EditScheduleAddress = ({
	list,
	schedule,
	onUpdate,
	selectedDeliveryAddress,
	setSelectedDeliveryAddress,
}: {
	list: ApiList;
	schedule: ApiSchedule;
	onUpdate: (address?: ApiDeliveryAddress) => void;
	selectedDeliveryAddress: string;
	setSelectedDeliveryAddress: React.Dispatch<React.SetStateAction<string>>;
}): JSX.Element => {
	const t = { ...useTranslation().subscriptions, generic: useTranslation().generic, form: useTranslation().form };
	const { data: deliveryAddresses, mutate: mutateDeliveryAddresses } = useApi(Res.deliveryAddresses, {});
	const form = useFormValidationContainer();
	const { api } = useConfig();
	const { user } = useSession();
	const deliveryType: DeliveryTypeCompany = 'door_delivery';

	const [addressSaving, setAddressSaving] = useState(false);
	const [addressPickerOpen, setAddressPickerOpen] = useState(false);
	const [showAddressForm, setShowAddressForm] = useState(false);
	const [editingDeliveryAddress, setEditingDeliveryAddress] = useState<ApiDeliveryAddress | undefined>(undefined);
	const addressFormRef = useRef<HTMLDivElement>(null);
	const selectedAddress =
		schedule?.deliveryAddress ??
		deliveryAddresses?.find((item) => item.id !== undefined && String(item.id) === selectedDeliveryAddress);

	const updateDeliveryAddress = async (addressId: string): Promise<void> => {
		if (!addressId) return;
		setSelectedDeliveryAddress(addressId);
		if (!schedule) return;
		if (String(schedule.deliveryAddress?.id) === addressId) return;
		const nextAddress =
			deliveryAddresses?.find((item) => item.id !== undefined && String(item.id) === addressId) ??
			(editingDeliveryAddress && String(editingDeliveryAddress.id) === addressId
				? editingDeliveryAddress
				: undefined);

		const payload = {
			...schedule,
			customerId: user?.id,
			deliveryAddressId: nextAddress?.id ?? addressId,
		};

		await axios.patch(`${api}me/lists/${list.id}/schedule.json`, payload, WITH_CREDENTIALS);
	};

	const buildPatch = (original: DeliveryAddressPayload, changed: DeliveryAddressPayload): PatchOp[] =>
		(Object.keys(original) as (keyof DeliveryAddressPayload)[])
			.filter((key) => changed[key] !== original[key] && changed[key] !== undefined)
			.map((key) => ({
				op: 'replace',
				path: '/' + String(key),
				value: changed[key] as string | number,
			}));

	const mapDeliveryAddressToPayload = (
		address: DeliveryAddressPayload | ApiDeliveryAddress
	): DeliveryAddressPayload => ({
		company: address.company,
		careOf: address.careOf,
		firstname: address.firstname,
		lastname: address.lastname,
		street: address.street,
		doorCode: address.doorCode,
		city: address.city,
		zip: address.zip,
		phone: address.phone,
	});

	const buildDeliveryAddressPayload = (): DeliveryAddressPayload => ({
		company: form.deliveryCompany.value,
		careOf: form.deliveryCareOf.value,
		firstname: form.deliveryFirstname.value,
		lastname: form.deliveryLastname.value,
		street: form.deliveryStreet.value,
		doorCode: form.deliveryDoorCode.value,
		city: form.deliveryCity.value,
		zip: form.deliveryZip.value,
		phone: form.deliveryPhone.value,
	});

	const openAddressModal = () => {
		if (!selectedDeliveryAddress && schedule.deliveryAddress?.id !== undefined) {
			const address = deliveryAddresses?.find(
				(item) => item.id !== undefined && String(item.id) === selectedDeliveryAddress
			);
			setEditingDeliveryAddress(address);
			setSelectedDeliveryAddress(String(schedule.deliveryAddress.id));
		} else {
			setEditingDeliveryAddress(undefined);
		}
		setShowAddressForm(false);
		setAddressPickerOpen(true);
	};

	const closeAddressModal = () => {
		setAddressPickerOpen(false);
		setShowAddressForm(false);
		setEditingDeliveryAddress(undefined);
	};

	const selectAddress = (addressId: string) => {
		const nextAddress =
			deliveryAddresses?.find((item) => item.id !== undefined && String(item.id) === addressId) ??
			(editingDeliveryAddress && String(editingDeliveryAddress.id) === addressId
				? editingDeliveryAddress
				: undefined);
		if (!nextAddress) return;
		setEditingDeliveryAddress(addressId ? nextAddress : undefined);
		form.fillAddressForm(nextAddress);
		setShowAddressForm(false);
		setSelectedDeliveryAddress(addressId);
	};

	const startNewAddress = () => {
		setEditingDeliveryAddress(undefined);
		setSelectedDeliveryAddress('');
		form.resetAddressForm();
		setShowAddressForm(true);
		setAddressPickerOpen(true);
	};

	const startEditAddress = () => {
		const address =
			deliveryAddresses?.find((item) => item.id !== undefined && String(item.id) === selectedDeliveryAddress) ??
			selectedAddress;
		if (!address) return;
		setEditingDeliveryAddress(address);
		form.fillAddressForm(address);
		setShowAddressForm(true);
	};

	const handleSelectSavedAddress = async () => {
		if (!selectedDeliveryAddress) return;
		await updateDeliveryAddress(selectedDeliveryAddress);
		setAddressPickerOpen(false);
		setShowAddressForm(false);
		setEditingDeliveryAddress(undefined);
	};

	const handleSaveDeliveryAddress = async () => {
		if (!form.validateDeliveryAddress({ deliveryType })) {
			if (addressFormRef.current) {
				addressFormRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
			}
			return;
		}
		setAddressSaving(true);
		const payload = buildDeliveryAddressPayload();
		let savedAddressId: ApiDeliveryAddress['id'] | undefined;
		try {
			if (editingDeliveryAddress?.id !== undefined) {
				const originalPayload = mapDeliveryAddressToPayload(editingDeliveryAddress);
				const patch = buildPatch(originalPayload, payload);
				if (patch.length > 0) {
					await axios.patch(
						`${api}me/delivery-addresses/${editingDeliveryAddress.id}.json`,
						patch,
						WITH_CREDENTIALS
					);
				}
				savedAddressId = editingDeliveryAddress.id;
			} else {
				const { data } = await axios.post(`${api}me/delivery-addresses.json`, payload, WITH_CREDENTIALS);
				savedAddressId = (data as { deliveryAddressId?: ApiDeliveryAddress['id'] })?.deliveryAddressId;
			}
			await mutateDeliveryAddresses();

			if (editingDeliveryAddress) {
				onUpdate(editingDeliveryAddress);
			}

			if (savedAddressId !== undefined) {
				await updateDeliveryAddress(String(savedAddressId));
			}
			setAddressPickerOpen(false);
			setShowAddressForm(false);
			setEditingDeliveryAddress(undefined);
		} finally {
			setAddressSaving(false);
		}
	};

	return (
		<>
			<div className="edit-schedule-address">
				{selectedAddress ? (
					<div className="schedule-delivery-address-summary">
						<AddressSummary address={selectedAddress} />
						<Button
							className="secondary schedule-delivery-address-summary-button"
							onClick={openAddressModal}
						>
							{t.generic.edit}
						</Button>
					</div>
				) : null}
				{!selectedAddress && (
					<div className="schedule-delivery-address-add-new">
						<Button className="secondary" onClick={startNewAddress}>
							{t.form.selectDeliveryAddress}
						</Button>
					</div>
				)}
			</div>

			<Modal
				className="content-card-modal schedule-delivery-address-modal"
				show={addressPickerOpen}
				disableBodyScroll
				onDismiss={closeAddressModal}
				toggle={closeAddressModal}
			>
				<ContentCard className="content schedule-delivery-address-modal-content">
					<div className="schedule-delivery-address-modal-header">
						<h2 className="schedule-delivery-address-modal-title">
							{selectedDeliveryAddress
								? showAddressForm
									? t.form.editAddress
									: t.form.changeAddress
								: t.form.addNewAddress}
						</h2>

						<button className="close-btn" type="button" onClick={closeAddressModal}>
							<Icon shape={{ ...close, fill: 'black' }} size={20} />
						</button>
					</div>
					<div className="modal-content">
						{deliveryAddresses && deliveryAddresses.length > 0 && (
							<div className="checkout-delivery-address-select">
								<CheckoutSelectDeliveryAddress
									selectedAddress={selectedDeliveryAddress}
									deliveryAddresses={deliveryAddresses}
									onSelectAddress={selectAddress}
								/>
							</div>
						)}
						{showAddressForm ? (
							<div className="checkout-delivery-address-form" ref={addressFormRef}>
								<DeliveryAddressInput
									deliveryType={deliveryType}
									deliveryAddresses={[]}
									selectedAddress={selectedDeliveryAddress}
									setSelectedAddress={setSelectedDeliveryAddress}
								/>
								<div className="checkout-delivery-address-form-actions">
									<Button type="button" disabled={addressSaving} onClick={handleSaveDeliveryAddress}>
										{addressSaving ? t.generic.saving : t.generic.save}
									</Button>
									{deliveryAddresses && deliveryAddresses.length > 0 && (
										<Button type="button" className="secondary" onClick={startNewAddress}>
											{t.form.addNewAddress}
										</Button>
									)}
									<Button type="button" className="secondary" onClick={closeAddressModal}>
										{t.generic.cancel}
									</Button>
								</div>
							</div>
						) : (
							<div className="checkout-delivery-address-form-actions">
								<Button
									type="button"
									disabled={!selectedDeliveryAddress}
									onClick={handleSelectSavedAddress}
								>
									{t.generic.select}
								</Button>
								{(selectedDeliveryAddress || selectedAddress) && (
									<Button type="button" className="secondary" onClick={startEditAddress}>
										{t.form.editAddress}
									</Button>
								)}
								<Button type="button" className="secondary" onClick={startNewAddress}>
									{t.form.addNewAddress}
								</Button>
								<Button type="button" className="secondary" onClick={closeAddressModal}>
									{t.generic.cancel}
								</Button>
							</div>
						)}
					</div>
				</ContentCard>
			</Modal>
		</>
	);
};
